import React, { useMemo } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { expandArrowColumn, HeadCellType, isNumberType } from '@fabric-ui/components/Table';
import { flex } from '@fabric-ui/theme';
import { emptyArray } from '@fabric-ui/utils';
import { Box, SxProps } from '@mui/material';
import { useGetHeadCellsWithRequestObject } from '@utils/RequestUtils';
import { getDimsWithAlias } from '@utils/utils';
import { ChartClickInteraction } from '../../../../../components/Widgets/types';
import { styleNoUpperCase } from '../../../../../context/utils';
import { useFormattingRules } from '../../../../FormattingRules/hooks';
import { getFormattingRuleById } from '../../../../FormattingRules/utils';
import { defaultDrillDownHierarchy, OTHERS } from '../../../constants';
import { getColumnFormatter } from '../../../CustomDashboardsUtils';
import { useGetDisplayName } from '../../../hooks/useGetDisplayName';
import { CommonWidgetProps, PivotTableTypeWidget, SortBy, SortDir, TableCategoryWidgetTypes, TableSubType } from '../../../types';
import { useDrillDownData } from '../../Interactions/useDrillDownData';
import { commonTableProps } from './constants';
import { ExpandedRow } from './ExpandedRow';
import PivotTable from './PivotTable';
import { checkIfClickInteractionsEnabled } from './PivotTable/utils';
import Table from './Table';
import { applyRowStyleForTable } from './utils';

export interface TableWidgetProps extends CommonWidgetProps {
  widget: TableCategoryWidgetTypes;
  totalRowCount?: number;
  clickInteractions?: ChartClickInteraction[];
  isEdit?: boolean;
  resizable?: boolean;
  sx?: SxProps;
}

const TableWidget: React.FC<TableWidgetProps> = ({
  widget,
  setWidget,
  data: originalData = emptyArray,
  isLoading = false,
  responseTotals,
  totalRowCount,
  onSelect,
  selected = emptyArray,
  clickInteractions,
  globalFilters,
  isEdit = false,
  resizable = true,
  sx = {},
}) => {
  const {
    drillDownHierarchy = defaultDrillDownHierarchy,
    requests: [request],
    definition: {
      custom: { crossTableView },
    },
  } = widget;
  const { data: formattingRules = [] } = useFormattingRules();

  const getDisplayName = useGetDisplayName(request.projectionId.name);
  const {
    data: drillDownData,
    totalRowCountDrillDown,
    isLoadingDrillDown,
    newQueryObject,
    allDrillDownFields,
    setDrillDownSort,
  } = useDrillDownData({
    widget,
    contextFilter: globalFilters,
    enabled: !crossTableView,
  });
  const showExpandedRow = crossTableView && request.select.dims.length === 1;
  const { size: rowLimit = 20 } = widget.requests[0].top || { size: 20 };
  const { activeIndex, drillDownValues = [] } = drillDownHierarchy;
  const nextDrillDownField = allDrillDownFields[activeIndex + 1];
  const queryHeadCells = useGetHeadCellsWithRequestObject(
    activeIndex !== 0 && !crossTableView
      ? {
          ...newQueryObject,
          select: { ...newQueryObject.select, dims: allDrillDownFields[activeIndex] ? [allDrillDownFields[activeIndex]] : [] },
        }
      : widget.requests[0]
  );

  const data = useMemo(
    () => (!!drillDownData && !crossTableView ? drillDownData : originalData),
    [crossTableView, drillDownData, originalData]
  );
  const activeRow = widget.drillDownHierarchy?.expendedIds || [];

  const headCells = useMemo(
    () => [
      { id: 'id', label: 'ID', hidden: true, isKey: true, sx: styleNoUpperCase } as HeadCellType,
      ...(showExpandedRow ? [expandArrowColumn(() => true)] : []),
      ...queryHeadCells.map((cell, index) => {
        const cellFieldStyle = widget.definition.custom.fieldStyle?.[cell.id];
        const formattingRuleById = getFormattingRuleById(
          cellFieldStyle?.formattingRuleId,
          formattingRules,
          widget?.definition.custom.applyFormattingToAll?.formattingRuleId
        );

        const metricsSet = new Set(request.select.metrics.map(({ name }) => name));

        return !crossTableView && index === 0 && !!nextDrillDownField && request.select.dims.length === 1
          ? {
              sx: styleNoUpperCase,
              ...cell,
              style: {
                transition: theme => theme.transitions.create(['background'], { duration: theme.transitions.duration.shorter }),
                ':hover': { background: 'var(--color-surface-table-row-active)' },
                cursor: 'pointer',
              },

              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: (value, row, setRows, index, rows, dimId) => (
                <AvTooltip disabled={!nextDrillDownField} title={`Drill Down to ${getDisplayName(nextDrillDownField?.name)}`}>
                  <Box
                    sx={{
                      ...(isNumberType(cell.type) ? { ...flex.row, flexDirection: 'row-reverse' } : flex.row),
                      height: '100%',
                      width: '100%',
                    }}
                    onClick={() => onActive(value, rows, dimId)}>
                    {getColumnFormatter({
                      value,
                      type: cell.type,
                      columnFormatter: cellFieldStyle?.columnFormatter,
                      formattingRule: formattingRuleById,
                    })}
                  </Box>
                </AvTooltip>
              ),
            }
          : checkIfClickInteractionsEnabled(metricsSet, cell.id, clickInteractions)
            ? {
                ...cell,
                sx: styleNoUpperCase,
                rowStyle: applyRowStyleForTable({
                  selected,
                  cell,
                  formattingRuleById,
                  cellFieldStyle,
                  hasClickInteraction: true,
                }),

                // eslint-disable-next-line react/no-unstable-nested-components
                formatter: (value, row) => (
                  <AvTooltip title={`Click to ${clickInteractions[0].title.toLowerCase()}`}>
                    <Box
                      sx={isNumberType(cell.type) ? { ...flex.row, flexDirection: 'row-reverse' } : flex.row}
                      onClick={e =>
                        clickInteractions[0].onClick({
                          row,
                          cell,
                          currentIndex: crossTableView ? 0 : activeIndex,
                          e,
                          widgetData: data,
                          originalValue: row[widget.requests[0].select.dims?.[0]?.name],
                          headCellType: headCells.find(({ id }) => id === widget.requests[0].select.dims?.[0]?.name)?.type,
                        })
                      }>
                      {getColumnFormatter({
                        value,
                        type: cell.type,
                        columnFormatter: cellFieldStyle?.columnFormatter,
                        formattingRule: formattingRuleById,
                      })}
                    </Box>
                  </AvTooltip>
                ),
              }
            : {
                ...cell,
                sx: styleNoUpperCase,
                rowStyle: applyRowStyleForTable({ selected, cell, formattingRuleById, cellFieldStyle }),
                formatter: value =>
                  getColumnFormatter({
                    value,
                    type: cell.type,
                    fieldName: cell.fieldName || cell.id,
                    columnFormatter: cellFieldStyle?.columnFormatter,
                    formattingRule: formattingRuleById,
                  }),
              };
      }),
    ],
    [queryHeadCells, drillDownHierarchy, crossTableView, request, selected]
  );
  const visibleHeadCells = useMemo(() => headCells.filter(({ hidden }) => !hidden), [headCells]);

  const onSort = (field: string, direction: 'asc' | 'desc' | null) => {
    if (setWidget) {
      const dimsWithAliases = getDimsWithAlias(widget.requests[0].select.dims, 'name');
      const isAsc = direction === 'asc';
      const sortBy: SortBy = {
        name: dimsWithAliases[field]?.alias ?? field,
        dir: isAsc ? SortDir.ASC : SortDir.DESC,
      };
      if (!crossTableView && activeIndex !== 0) {
        setDrillDownSort(sortBy);
      } else {
        setWidget({
          ...widget,
          requests: [
            {
              ...widget.requests[0],
              sorting: [sortBy],
              top: widget.requests[0].top ? { ...widget.requests[0].top, offset: 0 } : undefined,
            },
          ],
        });
      }
    }
  };

  const setOffset = (offset: number) => {
    if (setWidget) {
      setWidget({
        ...widget,
        requests: [
          {
            ...widget.requests[0],
            top: { ...widget.requests[0].top, offset },
          },
        ],
      });
    }
  };

  const onPaginationChange = page => setOffset(page * rowLimit);

  const onActive = (value, rows, dimId) => {
    if (setWidget) {
      if (nextDrillDownField) {
        setWidget({
          ...widget,
          requests: [{ ...request, top: { ...request.top, offset: 0 } }],
          drillDownHierarchy: {
            ...drillDownHierarchy,
            activeIndex: activeIndex + 1,
            drillDownValues:
              drillDownValues[activeIndex] !== undefined ? drillDownValues.with(activeIndex, value) : [...drillDownValues, value],
            notOthersDrillDownValues: value === OTHERS ? rows.filter(row => row[dimId] !== 'Others').map(row => row[dimId]) : [],
          },
        });
      }
    }
  };

  const onActiveCross = v => {
    if (setWidget && crossTableView) {
      if (!v && activeRow[0]) {
        setWidget({
          ...widget,
          requests: [{ ...request }],
          drillDownHierarchy: {
            ...drillDownHierarchy,
            expendedIds: [],
          },
        });
      }
      if (v) {
        const hadVal = drillDownValues[0] !== undefined;
        const value = data.find(({ id }) => id === v)[allDrillDownFields[0].name];
        setWidget({
          ...widget,
          requests: [request],
          drillDownHierarchy: {
            ...drillDownHierarchy,
            activeIndex: hadVal ? 0 : 1,
            drillDownValues: [value],
            expendedIds: [v],
          },
        });
      }
    }
  };

  const expandedRow = (row, onChange, isItemSelected) => (
    <ExpandedRow
      isItemSelected={isItemSelected || activeRow[0] === row.id}
      widget={widget}
      selected={selected}
      clickInteractions={clickInteractions}
      activeIndex={1}
      setDrillDownValues={v => setWidget({ ...widget, drillDownHierarchy: { ...drillDownHierarchy, drillDownValues: v } })}
      setWidget={setWidget}
      globalFilters={globalFilters}
    />
  );

  if (widget.type === TableSubType.Table) {
    return (
      <Table
        widget={widget}
        commonTableProps={commonTableProps}
        data={data}
        headCells={headCells}
        isLoading={isLoading}
        isLoadingDrillDown={isLoadingDrillDown}
        crossTableView={crossTableView}
        onSort={onSort}
        activeRow={activeRow}
        nextDrillDownField={nextDrillDownField}
        onSelect={onSelect}
        onActiveCross={onActiveCross}
        showExpandedRow={showExpandedRow}
        rowLimit={rowLimit}
        expandedRow={expandedRow}
        setOffset={setOffset}
        totalRowCount={totalRowCount}
        totalRowCountDrillDown={totalRowCountDrillDown}
        responseTotals={responseTotals}
        visibleHeadCells={visibleHeadCells}
      />
    );
  }

  return (
    <PivotTable
      widgetData={(widget as PivotTableTypeWidget).requests[0]}
      data={data}
      groupOthers={widget.requests[0].top?.groupOthers}
      offset={widget.requests[0].top?.offset}
      rowSize={widget.definition.custom.rowSize}
      showTotals={widget.definition.custom.showTotals}
      showColTotals={(widget as PivotTableTypeWidget).definition.custom.showColTotals}
      headCells={headCells}
      isLoading={isLoading}
      rowLimit={rowLimit}
      onPaginationChange={onPaginationChange}
      totalRowCount={totalRowCount}
      responseTotals={responseTotals}
      onSort={newSorting => setWidget({ ...widget, requests: [{ ...widget.requests[0], sorting: newSorting }] })}
      fieldStyle={widget.definition.custom.fieldStyle}
      formattingRuleId={widget?.definition.custom.applyFormattingToAll?.formattingRuleId}
      clickInteractions={clickInteractions}
      currentIndex={crossTableView ? 0 : activeIndex}
      selected={selected}
      isEditingWidget={isEdit}
      resizable={resizable}
      sx={sx}
    />
  );
};

export default TableWidget;
